import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import IncommingCallPopup from '../components/IncommingCallPopup';
import OutgoingCallPopup from '../components/OutgoingCallPopup';

/**
 * Public helpers (single line usage):
 * import { showIncomingCall } from '.../contexts/CallPopupContext';
 * showIncomingCall({ callerName: 'Alice', callerAvatar: 'url', onAccept: fn, onDecline: fn });
 */
let externalShowIncomingCall = () => { console.warn('CallPopupProvider not mounted yet'); };
let externalShowOutgoingCall = () => { console.warn('CallPopupProvider not mounted yet'); };
let externalHideCallPopup = () => { };

// Public API
// showIncomingCall(user, options?) OR showIncomingCall({ user, onAccept, onDecline, timeoutMs })
export const showIncomingCall = (user, options) => {
    if (user && typeof user === 'object' && !options && !user.user) {
        // Backwards compatible: first arg user, second arg options
        return externalShowIncomingCall({ user });
    }
    if (user && user.user) {
        // single object form already
        return externalShowIncomingCall(user);
    }
    return externalShowIncomingCall({ user, ...(options || {}) });
};
export const hideIncomingCall = () => externalHideCallPopup();
export const showOutgoingCall = (user, options) => {
    if (user && typeof user === 'object' && !options && !user.user) {
        return externalShowOutgoingCall({ user });
    }
    if (user && user.user) return externalShowOutgoingCall(user);
    return externalShowOutgoingCall({ user, ...(options || {}) });
};
export const hideOutgoingCall = () => externalHideCallPopup();

const CallPopupContext = createContext({
    showIncomingCall: () => { },
    hideIncomingCall: () => { },
    showOutgoingCall: () => { },
    hideOutgoingCall: () => { }
});

export const CallPopupProvider = ({ children }) => {
    const [incomingCallData, setIncomingCallData] = useState(null); // { user, onAccept, onDecline }
    const [outgoingCallData, setOutgoingCallData] = useState(null); // { user, onCancel, handlers }
    const timeoutRef = useRef(null);

    const hide = useCallback(() => {
        setIncomingCallData(null);
        setOutgoingCallData(null);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const showIncoming = useCallback((options) => {
        if (!options || !options.user) {
            console.warn('showIncomingCall: missing user object');
            return;
        }
        // clear any ongoing outgoing call popup
        setOutgoingCallData(null);
        setIncomingCallData({
            user: options.user,
            onAccept: options?.onAccept,
            onDecline: options?.onDecline,
        });
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        const t = options?.timeoutMs ?? 45000; // auto dismiss after 45s by default
        if (t > 0) {
            timeoutRef.current = setTimeout(() => hide(), t);
        }
    }, [hide]);

    const showOutgoing = useCallback((options) => {
        if (!options || !options.user) {
            console.warn('showOutgoingCall: missing user object');
            return;
        }
        // clear any incoming call popup
        setIncomingCallData(null);
        setOutgoingCallData({
            user: options.user,
            onCancel: options?.onCancel,
            onToggleMute: options?.onToggleMute,
            onToggleSpeaker: options?.onToggleSpeaker,
            onToggleVideo: options?.onToggleVideo,
            onOpenChat: options?.onOpenChat,
            initialMuted: options?.initialMuted || false,
            initialSpeaker: options?.initialSpeaker || false,
            initialVideo: options?.initialVideo || false,
        });
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        // Outgoing call usually not auto-dismissed unless timeoutMs specified
        const t = options?.timeoutMs;
        if (t && t > 0) {
            timeoutRef.current = setTimeout(() => hide(), t);
        }
    }, [hide]);

    useEffect(() => {
        externalShowIncomingCall = showIncoming;
        externalShowOutgoingCall = showOutgoing;
        externalHideCallPopup = hide;
    }, [showIncoming, showOutgoing, hide]);

    const value = {
        showIncomingCall: showIncoming,
        hideIncomingCall: hide,
        showOutgoingCall: showOutgoing,
        hideOutgoingCall: hide
    };

    return (
        <CallPopupContext.Provider value={value}>
            {children}
            {incomingCallData && !outgoingCallData && (
                <IncommingCallPopup
                    user={incomingCallData.user}
                    onAccept={() => { incomingCallData.onAccept?.(incomingCallData.user); hide(); }}
                    onDecline={() => { incomingCallData.onDecline?.(incomingCallData.user); hide(); }}
                    onClose={hide}
                />
            )}
            {outgoingCallData && !incomingCallData && (
                <OutgoingCallPopup
                    user={outgoingCallData.user}
                    onCancel={() => { outgoingCallData.onCancel?.(outgoingCallData.user); hide(); }}
                    onToggleMute={outgoingCallData.onToggleMute}
                    onToggleSpeaker={outgoingCallData.onToggleSpeaker}
                    onToggleVideo={outgoingCallData.onToggleVideo}
                    onOpenChat={outgoingCallData.onOpenChat}
                    initialMuted={outgoingCallData.initialMuted}
                    initialSpeaker={outgoingCallData.initialSpeaker}
                    initialVideo={outgoingCallData.initialVideo}
                    onClose={hide}
                />
            )}
        </CallPopupContext.Provider>
    );
};

export const useCallPopup = () => useContext(CallPopupContext);
